import { SecurePassAutoSend } from "@/utils/enum";
import { refreshToken, logout, startAccountImpersonation, endAccountImpersonation } from "@/utils/session";
import { getSessionStorage, setSessionStorage, getLocalStorage } from "@/utils/storage";
import { validateFeature } from "@/services/feature";
import { getMyNotificationSettings } from "@/services/notifications";
import { getAppConfigData, getUserLayoutConfig } from "@/services/user";

const ConfigsModel = {
  namespace: "configs",
  state: {
    configLang: {},
    configCurrentUser: {},
    configPerm: {},
    configNotifications: {},
    configMaintenanceMode: false,
    configFetched: false,
    devModeBypass: getSessionStorage("devModeBypass") || false,
    securePass: {},
    features: {},
    userLayout: {},
  },
  effects: {
    *getConfigData(_, { call, put }) {
      let conf = yield call(getAppConfigData);

      if (Object.keys(conf.auth.grantedPolicies).length === 0 && getLocalStorage("dataAuth") !== null) {
        // Try to refresh token
        yield call(refreshToken);
        conf = yield call(getAppConfigData);
      }

      if (Object.keys(conf.auth.grantedPolicies).length === 0) {
        // Nothing to do, logout
        yield put({ type: "updateLang", payload: {} });
        yield put({ type: "updateCurrentUser", payload: {} });
        yield put({ type: "updatePerm", payload: {} });
        yield put({ type: "updateMaintenanceMode", payload: {} });
        yield put({ type: "updateFetched", payload: false });
        yield put({ type: "updateFeatures", payload: {} });
        yield put({ type: "updateUserLayout", payload: {} });
        yield call(logout);
      }

      const userLayout = yield call(getUserLayoutConfig);
      const notificationSettings = yield call(getMyNotificationSettings);

      yield put({
        type: "updateLang",
        payload: conf.localization.values,
      });

      yield put({
        type: "updateCurrentUser",
        payload: conf.currentUser,
      });

      yield put({
        type: "updatePerm",
        payload: conf.auth.grantedPolicies,
      });

      yield put({
        type: "updateMaintenanceMode",
        payload: conf?.features?.values?.["MaintenanceMode.Enabled"] === "true",
      });

      yield put({
        type: "updateFetched",
        payload: true,
      });

      const securePassAutoSend = Number(conf?.features?.values?.["SecurePass.AutosendType"]);
      const securePassThreshold = Number(conf?.features?.values?.["SecurePass.VehicleMSRPThreshold"]);

      yield put({
        type: "updateSecurePass",
        payload: {
          enabled: securePassAutoSend !== SecurePassAutoSend.NOT_SEND,
          autoSend: securePassAutoSend,
          threshold: securePassThreshold,
        },
      });

      yield put({
        type: "updateFeatures",
        payload: conf.features.values,
      });

      yield put({
        type: "updateUserLayout",
        payload: userLayout,
      });

      yield put({
        type: "updateNotificationSettings",
        payload: notificationSettings,
      });
    },
    *validateDevKey({ payload }, { call, put }) {
      try {
        const isValid = yield call(validateFeature, payload);

        yield put({
          type: "updateDevModeBypass",
          payload: isValid,
        });

        return { error: !isValid };
      } catch (error) {
        yield put({
          type: "updateDevModeBypass",
          payload: false,
        });
        return { error: true };
      }
    },
    *handleStartUserImpersonation({ payload }, { call, select }) {
      const configPerm = yield select(state => state.configs.configPerm);
      yield call(startAccountImpersonation, { ...payload, configPerm });
    },
    *handleEndUserImpersonation({ payload }, { call }) {
      yield call(endAccountImpersonation, payload);
    },
  },
  reducers: {
    updateLang(state, action) {
      return { ...state, configLang: action.payload };
    },
    updateCurrentUser(state, action) {
      return { ...state, configCurrentUser: action.payload };
    },
    updatePerm(state, action) {
      return { ...state, configPerm: action.payload };
    },
    updateMaintenanceMode(state, action) {
      return { ...state, configMaintenanceMode: action.payload };
    },
    updateFetched(state, action) {
      return { ...state, configFetched: action.payload };
    },
    updateDevModeBypass(state, action) {
      // only save to session storage if it's true
      if (action.payload) {
        setSessionStorage("devModeBypass", action.payload);
      }

      return { ...state, devModeBypass: action.payload };
    },
    updateSecurePass(state, action) {
      return { ...state, securePass: action.payload };
    },
    updateFeatures(state, action) {
      return { ...state, features: action.payload };
    },
    updateUserLayout(state, action) {
      return { ...state, userLayout: action.payload };
    },
    updateNotificationSettings(state, action) {
      return { ...state, configNotifications: action.payload };
    },
  },
};
export default ConfigsModel;
